import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { Icon, type IconName } from './Icon';

/**
 * The handheld floating action button, and the slot the shell positions it in
 * (ADR 0048 §3.1, §8).
 *
 * Routes render `<Fab>` themselves — the mail list's Compose, the calendar's
 * New event, the contacts list's New contact — because only the route knows
 * what its primary action does. The shell renders one `<FabSlot>` and owns
 * *where* the button sits: above the bottom bar and the home indicator on the
 * screens that have the bar, and nowhere on the others. A `<Fab>` is portalled
 * into that slot, so no route has to know the bar's height, and a FAB rendered
 * deep inside a route cannot be trapped by a transformed or clipped ancestor.
 *
 * The slot element is a module-level store rather than context: there is one
 * shell, and a store lets a route's `<Fab>` find the slot without a provider
 * wrapped round every route (and every route test). Outside the handheld shell
 * there is no slot, and `<Fab>` renders nothing — on desktop the same action is
 * the sidebar's or the toolbar's button.
 */
let slot: HTMLElement | null = null;
let mountedFabs = 0;
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}
function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
const getSlot = () => slot;
const getMountedFabs = () => mountedFabs;

export interface FabProps {
  icon: IconName;
  /** The accessible name; also the visible text when `extended`. */
  label: string;
  onClick: () => void;
  /** Icon and text ("✎ Compose") instead of a 56-px round icon button. */
  extended?: boolean;
}

export function Fab({ icon, label, onClick, extended = false }: FabProps) {
  const target = useSyncExternalStore(subscribe, getSlot, getSlot);

  // Counted so the shell's fallback FAB steps aside the moment a route
  // provides its own (see `FabSlot`).
  useEffect(() => {
    mountedFabs += 1;
    emit();
    return () => {
      mountedFabs -= 1;
      emit();
    };
  }, []);

  if (!target) return null;
  return createPortal(<FabButton icon={icon} label={label} onClick={onClick} extended={extended} />, target);
}

function FabButton({ icon, label, onClick, extended = false }: FabProps) {
  return (
    <button
      type="button"
      className="fab"
      data-extended={extended ? 'true' : 'false'}
      aria-label={extended ? undefined : label}
      onClick={onClick}
    >
      <Icon name={icon} size={24} />
      {extended && <span className="fab__label">{label}</span>}
    </button>
  );
}

/**
 * The target `<Fab>` portals into. Rendered once, by the shell, on screens that
 * have a FAB.
 *
 * `fallback` is shown only while no route has mounted a `<Fab>`. The shell uses
 * it for Compose on the mail list: on handheld the sidebar and its Compose
 * button are not rendered, so without it there would be no way to start a
 * message until the mail list supplies its own.
 */
export function FabSlot({ fallback }: { fallback?: FabProps | undefined }) {
  const routeFabs = useSyncExternalStore(subscribe, getMountedFabs, getMountedFabs);
  const ref = useCallback((element: HTMLDivElement | null) => {
    slot = element;
    emit();
  }, []);

  return (
    <div className="fab-slot" ref={ref}>
      {/* A plain button, not a `<Fab>`: a `<Fab>` counts itself as a route
          FAB, and the fallback would hide itself the moment it mounted. */}
      {routeFabs === 0 && fallback && <FabButton {...fallback} />}
    </div>
  );
}
